import { Alert } from '@mui/material';
import { useLazyUserQuery } from 'apis/user/api';
import { setUser } from 'apis/user/store';
import { KycStatus, UserAccessLevel } from 'apis/user/types';
import {
  useActiveExchange,
  useCurrentUser,
  useTraderKycStatusQuery,
  useUserKycStatusQuery,
} from 'apis/user/use';
import AnchorLink from 'components/AnchorLink';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import {
  ROUTE_2FA,
  ROUTE_BECOME_TRADER,
  ROUTE_KYC,
  ROUTE_SUBSCRIPTIONS,
} from 'routes';
import { isFeatureOn } from 'whitelabel';
import { Features } from 'whitelabel/type';
import { format } from 'date-fns';
import { SUBSCRIPTIONS_CHECK_INTERVAL } from '../Checkers/constants';
import { useTraderServices } from '../../../apis/service/use';
import {
  getTraderKycStatus,
  isKycExpiredOrExpiring,
} from '../../../views/TraderService/components/BecomeTraderLanding/util';
import { useCoinBalances } from '../../../apis/coin/use';
import { InfoTooltip } from './atoms';

const RequiredActionsBanner = () => {
  const user = useCurrentUser();
  const exchange = useActiveExchange();
  const { data: balance } = useCoinBalances();

  const [loadUser] = useLazyUserQuery();
  const skipKyc = !exchange?.internalId || !isFeatureOn(Features.Kyc);

  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { data: traderServices } = useTraderServices();

  useAsync(async () => {
    // Refresh user data in case of accessLevel updated from outside.
    // And probably good to have up to date user data.
    const userData = await loadUser().unwrap();
    dispatch(setUser(userData));
  }, []);

  const { data: userKyc, isLoading: userKycLoading } = useUserKycStatusQuery({
    skip: skipKyc,
  });

  const { data: traderKyc, isLoading: traderKycLoading } =
    useTraderKycStatusQuery({
      skip: skipKyc || !traderServices?.length,
    });

  const {
    kycExpiryDate: userKycExpiryDate,
    expiring: userKycExpiringSoon,
    expired: userKycExpired,
  } = isKycExpiredOrExpiring(userKyc);

  const {
    kycExpiryDate: traderKycExpiryDate,
    expiring: traderKycExpiringSoon,
    expired: traderKycExpired,
  } = getTraderKycStatus(traderKyc);

  const noKyc = userKyc?.statuses.some(
    (s) =>
      s.category === 'KYC' &&
      s.level === 1 &&
      [
        KycStatus.NOT_STARTED,
        KycStatus.INIT,
        KycStatus.PENDING,
        KycStatus.REJECTED_RETRY,
        KycStatus.EXPIRED,
        KycStatus.REJECTED,
      ].includes(s.status),
  );

  const needsKyc = isFeatureOn(Features.Kyc) && noKyc;

  const needs2FA = +balance?.USDT?.balanceTotal > 0 && !user['2FAEnable'];

  const expiredSubscription =
    isFeatureOn(Features.Subscriptions) &&
    user.accessLevel === UserAccessLevel.SubscriptionExpired;

  // Periodic refresh of user data to handle subscriptions status
  useEffect(() => {
    if (!isFeatureOn(Features.Subscriptions)) return;

    const intervalId = setInterval(async () => {
      const userData = await loadUser().unwrap();
      dispatch(setUser(userData));
    }, SUBSCRIPTIONS_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [user.subscriptionFinishesAt]);

  if (
    userKycLoading ||
    traderKycLoading ||
    (!needsKyc &&
      !needs2FA &&
      !expiredSubscription &&
      !(
        traderKycExpiringSoon ||
        traderKycExpired ||
        userKycExpiringSoon ||
        userKycExpired
      ))
  )
    return null;

  return (
    <Alert
      severity={expiredSubscription ? 'error' : 'warning'}
      id='action-banner'
    >
      {needsKyc ? (
        <Trans
          t={t}
          i18nKey={
            {
              [KycStatus.REJECTED_RETRY]: 'pass-kyc-rejected-no-retry',
              [KycStatus.REJECTED]: 'pass-kyc-rejected',
              [KycStatus.INIT]: 'pass-kyc-in-progress',
              [KycStatus.PENDING]: 'pass-kyc-in-progress',
              [KycStatus.EXPIRED]: 'pass-kyc-expired',
              [KycStatus.NOT_STARTED]: 'pass-kyc',
            }[userKyc?.statuses?.[0]?.status || KycStatus.NOT_STARTED]
          }
        >
          <AnchorLink to={ROUTE_KYC} target={'_self'} />
          {!userKyc?.statuses?.[0]?.reason ? (
            <></>
          ) : (
            <InfoTooltip text={userKyc?.statuses?.[0]?.reason} />
          )}
        </Trans>
      ) : expiredSubscription ? (
        <Trans t={t} i18nKey={'subscription-expired'}>
          <AnchorLink to={ROUTE_SUBSCRIPTIONS} />
        </Trans>
      ) : userKycExpiringSoon || userKycExpired ? (
        <Trans
          t={t}
          i18nKey={userKycExpiringSoon ? 'kyc-expiring' : 'kyc-expired-date'}
          values={{
            date: format(new Date(userKycExpiryDate!), 'PPP p'),
          }}
        >
          <AnchorLink to={ROUTE_KYC} target={'_self'} />
        </Trans>
      ) : traderKycExpiringSoon || traderKycExpired ? (
        <Trans
          t={t}
          i18nKey={
            traderKycExpiringSoon ? 'kyc-wm-expiring' : 'kyc-wm-expired-date'
          }
          values={{
            date: format(new Date(traderKycExpiryDate!), 'PPP p'),
          }}
        >
          <AnchorLink
            to={ROUTE_BECOME_TRADER}
            target={'_self'}
            state={{ openKyc: true }}
          />
        </Trans>
      ) : (
        <Trans t={t} i18nKey={'secure-account'}>
          <AnchorLink to={ROUTE_2FA} target={'_self'} />
        </Trans>
      )}
    </Alert>
  );
};

export default RequiredActionsBanner;
